import { useStytch, useStytchUser } from "@stytch/react";
import { applyActAs } from "@/lib/testActAs";
import { freshSessionJwt, forceSessionRefresh } from "@/lib/stytch-session";
import { useCallback, useMemo } from "react";
import type {
  Document,
  DocumentWithBlocks,
  Block,
  Template,
  TemplateWithBlocks,
  UserPreferences,
  Job,
  Asset,
  CreateDocumentInput,
  UpdateDocumentInput,
  ImportJobInput,
  ExportJobInput,
  CreateTemplateInput,
  GetUploadUrlInput,
  RegisterAssetInput,
  AutoSaveInput,
  AutoSaveResponse,
  DocumentVersion,
} from "./api";

const API_BASE = "/api";

interface ApiError {
  message: string;
  code?: string;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      message: response.status === 401 ? "Unauthorized" : "An unexpected error occurred",
    }));
    throw new Error(error.message);
  }
  return response.json();
}

export function useApiClient() {
  const stytch = useStytch();
  const { user } = useStytchUser();
  const isSignedIn = !!user;

  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const send = async (token: string | undefined) => {
        const headers = applyActAs(new Headers(options.headers));
        if (token) headers.set("Authorization", `Bearer ${token}`);
        headers.set("Content-Type", "application/json");
        return fetch(url, { ...options, headers, credentials: "include" });
      };

      // Renewed before use when near expiry; the 401 retry below covers a
      // token rejected for reasons `exp` cannot show. This path previously had
      // neither, so a backgrounded tab simply failed.
      const response = await send(await freshSessionJwt(stytch as never));
      if (response.status !== 401 || !isSignedIn) return response;

      await forceSessionRefresh(stytch as never);
      return send(await freshSessionJwt(stytch as never));
    },
    [stytch, isSignedIn]
  );

  const api = useMemo(
    () => ({
      // Documents
      documents: {
        list: async () => {
          const response = await fetchWithAuth(`${API_BASE}/documents`);
          const documents = await handleResponse<Document[]>(response);
          return { documents };
        },

        get: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/documents/${id}`);
          const document = await handleResponse<DocumentWithBlocks>(response);
          return { document };
        },

        create: async (data: CreateDocumentInput) => {
          const response = await fetchWithAuth(`${API_BASE}/documents`, {
            method: "POST",
            body: JSON.stringify(data),
          });
          const document = await handleResponse<Document>(response);
          return { document };
        },

        update: async (id: string, data: UpdateDocumentInput) => {
          const response = await fetchWithAuth(`${API_BASE}/documents/${id}`, {
            method: "PUT",
            body: JSON.stringify(data),
          });
          const document = await handleResponse<Document>(response);
          return { document };
        },

        delete: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/documents/${id}`, {
            method: "DELETE",
          });
          if (response.ok) {
            return { success: true };
          }
          throw new Error("Failed to delete document");
        },

        autoSave: async (id: string, data: AutoSaveInput) => {
          // Auto-save uses the blocks batch update endpoint
          const response = await fetchWithAuth(`${API_BASE}/documents/${id}/blocks/batch`, {
            method: "POST",
            body: JSON.stringify({ blocks: data.blocks }),
          });
          await handleResponse<Block[]>(response);

          // Also update document title if changed
          if (data.title) {
            await fetchWithAuth(`${API_BASE}/documents/${id}`, {
              method: "PUT",
              body: JSON.stringify({ title: data.title }),
            });
          }

          return {
            document: {
              id,
              title: data.title ?? "",
              status: data.status ?? "saved",
              lastAutoSavedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            } as AutoSaveResponse
          };
        },

        versions: {
          list: async (documentId: string, params?: { limit?: number; offset?: number }) => {
            const searchParams = new URLSearchParams();
            if (params?.limit) searchParams.set("limit", String(params.limit));
            if (params?.offset) searchParams.set("offset", String(params.offset));

            const response = await fetchWithAuth(
              `${API_BASE}/documents/${documentId}/versions?${searchParams.toString()}`
            );
            const versions = await handleResponse<DocumentVersion[]>(response);
            return { versions };
          },

          create: async (documentId: string, data?: { comment?: string }) => {
            const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/versions`, {
              method: "POST",
              body: JSON.stringify({ name: data?.comment }),
            });
            const version = await handleResponse<DocumentVersion>(response);
            return { version };
          },

          restore: async (documentId: string, versionId: string) => {
            const response = await fetchWithAuth(
              `${API_BASE}/documents/${documentId}/versions/${versionId}/restore`,
              {
                method: "POST",
              }
            );
            const document = await handleResponse<DocumentWithBlocks>(response);
            return { document };
          },
        },
      },

      // Jobs - handled by lilia-editor-api (C#/.NET)
      jobs: {
        list: async (params?: { status?: string; jobType?: string }) => {
          const searchParams = new URLSearchParams();
          if (params?.status) searchParams.set("status", params.status);
          if (params?.jobType) searchParams.set("jobType", params.jobType);

          const response = await fetchWithAuth(
            `${API_BASE}/lilia/jobs?${searchParams.toString()}`
          );
          return handleResponse<{ jobs: Job[] }>(response);
        },

        get: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/lilia/jobs/${id}`);
          return handleResponse<{ job: Job }>(response);
        },

        retry: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/lilia/jobs/${id}/retry`, {
            method: "POST",
          });
          return handleResponse<{ job: Job }>(response);
        },

        import: async (data: ImportJobInput) => {
          const response = await fetchWithAuth(`${API_BASE}/lilia/jobs/import`, {
            method: "POST",
            body: JSON.stringify(data),
          });
          return handleResponse<{ job: Job; document?: Document }>(response);
        },

        export: async (data: ExportJobInput) => {
          const response = await fetchWithAuth(`${API_BASE}/lilia/jobs/export`, {
            method: "POST",
            body: JSON.stringify(data),
          });
          return handleResponse<{ job: Job; content: string; filename: string }>(
            response
          );
        },
      },

      // Templates
      templates: {
        list: async (params?: { category?: string }) => {
          const searchParams = new URLSearchParams();
          if (params?.category) searchParams.set("category", params.category);

          const response = await fetchWithAuth(
            `${API_BASE}/templates?${searchParams.toString()}`
          );
          const templates = await handleResponse<Template[]>(response);
          return { templates };
        },

        get: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/templates/${id}`);
          const template = await handleResponse<TemplateWithBlocks>(response);
          return { template };
        },

        create: async (data: CreateTemplateInput) => {
          const response = await fetchWithAuth(`${API_BASE}/templates`, {
            method: "POST",
            body: JSON.stringify(data),
          });
          const template = await handleResponse<Template>(response);
          return { template };
        },

        delete: async (id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/templates/${id}`, {
            method: "DELETE",
          });
          if (response.ok) {
            return { success: true };
          }
          throw new Error("Failed to delete template");
        },
      },

      // Preferences
      preferences: {
        get: async () => {
          const response = await fetchWithAuth(`${API_BASE}/preferences`);
          const preferences = await handleResponse<UserPreferences>(response);
          return { preferences };
        },

        update: async (data: Partial<UserPreferences>) => {
          const response = await fetchWithAuth(`${API_BASE}/preferences`, {
            method: "PUT",
            body: JSON.stringify(data),
          });
          const preferences = await handleResponse<UserPreferences>(response);
          return { preferences };
        },
      },

      // AI
      ai: {
        generateBlock: async (prompt: string, context?: { documentTitle?: string; surroundingText?: string }) => {
          const response = await fetchWithAuth(`${API_BASE}/ai/generate-block`, {
            method: "POST",
            body: JSON.stringify({ prompt, context }),
          });
          return handleResponse<{ block: { type: string; content: Record<string, unknown> } }>(response);
        },

        improveText: async (text: string, action: string = "improve") => {
          const response = await fetchWithAuth(`${API_BASE}/ai/improve-text`, {
            method: "POST",
            body: JSON.stringify({ text, action }),
          });
          return handleResponse<{ text: string }>(response);
        },

        suggestEquation: async (description: string) => {
          const response = await fetchWithAuth(`${API_BASE}/ai/suggest-equation`, {
            method: "POST",
            body: JSON.stringify({ description }),
          });
          return handleResponse<{ latex: string; block?: Record<string, unknown> }>(response);
        },

        parseLatex: async (latex: string) => {
          const response = await fetchWithAuth(`${API_BASE}/convert/latex-to-blocks`, {
            method: "POST",
            body: JSON.stringify({ latex }),
          });
          return handleResponse<{
            blocks: Array<{ type: string; content: Record<string, unknown> }>;
            title?: string;
            warnings?: string[];
          }>(response);
        },
      },

      // Assets
      assets: {
        getUploadUrl: async (documentId: string, data: GetUploadUrlInput) => {
          const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/assets`, {
            method: "POST",
            body: JSON.stringify({
              fileName: data.filename,
              fileType: data.mimeType,
              fileSize: data.size,
            }),
          });
          const result = await handleResponse<{ id: string; uploadUrl: string; publicUrl: string }>(response);
          return { uploadUrl: result.uploadUrl, assetId: result.id, s3Key: result.publicUrl };
        },

        register: async (_data: RegisterAssetInput) => {
          // Assets are registered when created via getUploadUrl in our new API
          // This is a no-op for compatibility
          return { asset: {} as Asset };
        },

        get: async (documentId: string, id: string) => {
          const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/assets/${id}`);
          const asset = await handleResponse<Asset>(response);
          return { asset, url: (asset as Asset & { url?: string }).url ?? "" };
        },
      },
    }),
    [fetchWithAuth]
  );

  return { api, isSignedIn, fetchWithAuth };
}
